import { Box, Divider, Grid, TextField } from '@mui/material';
import useCachedProfessionalBodiesList from '@src/adapters/hooks/professionalBodies/useCachedProfessionalBodiesList';
import { DropdownOption } from '@src/components/DropDown';
import ControlledProfessionalBodiesAutoComplete from '@src/components/professionalBodies/ControlledProfessionalBodiesAutoComplete';
import RolesAutoComplete from '@src/components/user/RolesAutoComplete';
import { UserRoles } from '@src/constants/UserRoles';
import { enqueueSnackbar } from 'notistack';
import { useEffect, useState } from 'react';

import { Control, FieldErrors, UseFormRegister } from 'react-hook-form';

interface HospitalUserFormValues {
  firstName: string;
  lastName: string;
  roles: string[];
  gmcNumber: string;
  professionalBodyId: string;
}

interface HospitalUserFormProps {
  formId?: string;
  fieldClassName?: string;
  register: UseFormRegister<HospitalUserFormValues>;
  control: Control<HospitalUserFormValues>;
  onSubmit?: (e: React.FormEvent<HTMLFormElement>) => void;
  readOnly: boolean;
  setRoles?: (value: string[]) => void;
  roles: string[];
  errors?: FieldErrors<HospitalUserFormValues>;
  currentProfessionalBodyId?: string;
}

const readOnlySx = {
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },

  '& .MuiOutlinedInput-input': {
    typography: 'body1',
    pt: 0,
    pl: 2,
    pb: 0,
  },
};

function HospitalUserForm({
  formId,
  register,
  onSubmit,
  readOnly = false,
  fieldClassName,
  roles,
  control,
  setRoles,
  errors,
  currentProfessionalBodyId,
}: HospitalUserFormProps): JSX.Element {
  const [professionalBodiesList, setProfessionalBodiesList] = useState<
    DropdownOption[]
  >([]);
  const { getList } = useCachedProfessionalBodiesList();

  useEffect(() => {
    (async () => {
      try {
        const response = await getList();
        const sorted = [...response]
          .sort((a, b) => a.order - b.order)
          .map((p) => ({ id: p.id, name: p.name }));

        setProfessionalBodiesList(sorted);
      } catch (error) {
        enqueueSnackbar('Failed to load professional bodies', {
          variant: 'error',
        });
      }
    })();
  }, [getList]);

  const selectedProfessionalBody = professionalBodiesList?.find(
    (body) => body.id === currentProfessionalBodyId
  );

  return (
    <form id={formId} onSubmit={onSubmit}>
      <Box sx={{ height: '100%', pt: 4, pb: 4, px: readOnly ? 0 : 3 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="First name"
              {...register('firstName', {
                required: 'First name is required',
              })}
              error={!!errors?.firstName}
              helperText={errors?.firstName?.message}
              inputProps={{ autoComplete: 'off' }}
              className={readOnly ? fieldClassName : undefined}
              InputLabelProps={{ shrink: true }}
              InputProps={{ readOnly }}
              sx={readOnly ? readOnlySx : undefined}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Last name"
              {...register('lastName', {
                required: 'Last name is required',
              })}
              error={!!errors?.lastName}
              helperText={errors?.lastName?.message}
              inputProps={{ autoComplete: 'off' }}
              className={readOnly ? fieldClassName : undefined}
              InputLabelProps={{ shrink: true }}
              InputProps={{ readOnly }}
              sx={readOnly ? readOnlySx : undefined}
            />
          </Grid>

          {readOnly && (
            <Grid item xs={12}>
              <Divider />
            </Grid>
          )}

          <Grid item xs={12} sm={6}>
            <ControlledProfessionalBodiesAutoComplete
              fieldName={'professionalBodyId'}
              control={control}
              readOnly={readOnly}
              fieldClassName={readOnly ? fieldClassName : undefined}
              readOnlySx={readOnly ? readOnlySx : undefined}
              professionalBodiesList={professionalBodiesList}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Registration number"
              {...register('gmcNumber', {
                validate: (value) =>
                  !currentProfessionalBodyId ||
                  !!value ||
                  `${selectedProfessionalBody?.name} number is required`,
              })}
              error={!!errors?.gmcNumber}
              helperText={errors?.gmcNumber?.message}
              inputProps={{ autoComplete: 'off' }}
              className={readOnly ? fieldClassName : undefined}
              InputProps={{ readOnly }}
              sx={readOnly ? readOnlySx : undefined}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {readOnly && (
            <Grid item xs={12}>
              <Divider />
            </Grid>
          )}

          <Grid item xs={12}>
            {readOnly || !setRoles ? (
              <TextField
                fullWidth
                label="Role"
                inputProps={{ autoComplete: 'off' }}
                className={readOnly ? fieldClassName : undefined}
                InputLabelProps={{ shrink: true }}
                value={roles}
                sx={readOnly ? readOnlySx : undefined}
              />
            ) : (
              <RolesAutoComplete
                required
                selection={setRoles}
                userRoles={roles}
                options={UserRoles}
                variant="outlined"
                size="medium"
                isRequired
              />
            )}
          </Grid>
        </Grid>
      </Box>
    </form>
  );
}

export default HospitalUserForm;
